import { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro"

// A saved view is a named URL param set, nothing more — applying one is
// exactly "paste this link". The expanded-student param stays out of views:
// it is transient reading position, not scope.
export const VIEW_PARAM_KEYS = [
  "tab",
  "q",
  "flagged",
  "signal",
  "mine",
] as const

export type ViewParamKey = (typeof VIEW_PARAM_KEYS)[number]
export type ViewParams = Partial<Record<ViewParamKey, string>>

// Built-in views are definitional, not stored: each one IS a param set (the
// status scopes plus the attention and mentor-is-me predicates), so they can
// never drift and never carry a kebab. "My students" exists only for users
// who hold a mentor role — for anyone else "mentor is me" is an empty set.
export type BuiltInView = { id: string; name: string; params: ViewParams }

// "My students" leads the row whenever it exists (Mayer: "I would also put
// my students as the first pill") — my-people-first is the natural reading
// order for anyone who has people.
export function buildBuiltInViews(hasMentorRole: boolean): BuiltInView[] {
  const views: BuiltInView[] = [
    { id: "builtin:active", name: "Active", params: {} },
    {
      id: "builtin:attention",
      name: "Needs attention",
      params: { flagged: "1" },
    },
    { id: "builtin:ended", name: "Completed", params: { tab: "ended" } },
    { id: "builtin:upcoming", name: "Upcoming", params: { tab: "upcoming" } },
  ]
  if (hasMentorRole) {
    views.unshift({
      id: "builtin:mine",
      name: "My students",
      params: { mine: "1" },
    })
  }
  return views
}

export function isBuiltInViewId(viewId: string | null): boolean {
  return viewId?.startsWith("builtin:") ?? false
}

export interface SavedView {
  id: string
  name: string
  params: ViewParams
  // Optional DataGrid column filter, carried only when the user chose
  // "Include column filters" on save — this is what lets views fully replace
  // the old grid-only Save filter feature.
  gridFilter: GridFilterModel | null
  createdAt: string
  updatedAt: string
}

interface StoredViews {
  version: 1
  views: SavedView[]
}

// Per-user suffix because back-office browsers get shared; the version field
// lets a future schema bump migrate instead of guessing.
const VIEWS_KEY_PREFIX = "studentsTab.views.v1"
// The pre-views feature saved only the DataGrid filter model, and was not
// per-user. It stays in localStorage after migration (rollback safety).
const LEGACY_FILTERS_KEY = "studentsTab.savedFilters"

function viewsStorageKey(userClientId: string): string {
  return `${VIEWS_KEY_PREFIX}:${userClientId}`
}

// Search (`q`) stays out of matching: it is a transient lookup lens, not part
// of a view's definition — a live query must not deselect the scope pill or
// drift a saved view. Legacy views that stored a q still APPLY it on click;
// it just never counts for equality.
const MATCHED_PARAM_KEYS = VIEW_PARAM_KEYS.filter((key) => key !== "q")

export function paramsEqual(a: ViewParams, b: ViewParams): boolean {
  return MATCHED_PARAM_KEYS.every(
    (key) => (a[key] ?? null) === (b[key] ?? null),
  )
}

// A filter item only counts once it can cut rows — a half-built panel row
// with no value yet is not a predicate. isEmpty/isNotEmpty are valueless by
// design.
export function isEffectiveFilterItem(item: GridFilterItem): boolean {
  return (
    item.operator === "isEmpty" ||
    item.operator === "isNotEmpty" ||
    (item.value !== undefined && item.value !== null && item.value !== "")
  )
}

export function effectiveFilterItems(
  model: GridFilterModel | null,
): GridFilterItem[] {
  return (model?.items ?? []).filter(isEffectiveFilterItem)
}

// Definitional equality for the column-filter half of a view: the same
// effective predicates, regardless of item ids or ordering. Pill matching and
// the drift affordances run on this, so a view lights up (or dots) for what
// its filters MEAN, not for how the model object happens to be shaped.
export function filterModelsEquivalent(
  a: GridFilterModel | null,
  b: GridFilterModel | null,
): boolean {
  const itemsA = effectiveFilterItems(a)
  const itemsB = effectiveFilterItems(b)
  if (itemsA.length !== itemsB.length) return false
  const signature = (item: GridFilterItem) =>
    JSON.stringify([item.field, item.operator, item.value ?? null])
  const signaturesA = itemsA.map(signature).sort()
  const signaturesB = itemsB.map(signature).sort()
  if (signaturesA.some((sig, index) => sig !== signaturesB[index])) return false
  // The join only matters once there are two or more predicates; AND is the
  // grid's default when the model omits it.
  if (itemsA.length > 1) {
    return (a?.logicOperator ?? "and") === (b?.logicOperator ?? "and")
  }
  return true
}

// Keep only the keys a view may legally hold — a hand-edited localStorage
// entry must not smuggle arbitrary params into the URL.
function sanitizeParams(raw: unknown): ViewParams {
  const params: ViewParams = {}
  if (typeof raw !== "object" || raw === null) return params
  for (const key of VIEW_PARAM_KEYS) {
    const value = (raw as Record<string, unknown>)[key]
    if (typeof value === "string" && value !== "") {
      params[key] = value
    }
  }
  return params
}

function sanitizeView(raw: unknown): SavedView | null {
  if (typeof raw !== "object" || raw === null) return null
  const view = raw as Record<string, unknown>
  if (typeof view.id !== "string" || typeof view.name !== "string") return null
  return {
    id: view.id,
    name: view.name,
    params: sanitizeParams(view.params),
    gridFilter: (view.gridFilter as GridFilterModel | null) ?? null,
    createdAt:
      typeof view.createdAt === "string"
        ? view.createdAt
        : new Date().toISOString(),
    updatedAt:
      typeof view.updatedAt === "string"
        ? view.updatedAt
        : new Date().toISOString(),
  }
}

// On first read for a user, legacy saved grid filters convert into views that
// hold no params and only a gridFilter — same name, new home. The legacy key
// was not per-user, so the conversion lands with whichever user opens the
// page first on that browser (accepted quirk, documented in the spec).
function migrateLegacyFilters(): SavedView[] {
  const legacyRaw = localStorage.getItem(LEGACY_FILTERS_KEY)
  if (!legacyRaw) return []
  try {
    const legacy = JSON.parse(legacyRaw)
    if (!Array.isArray(legacy)) return []
    const now = new Date().toISOString()
    return legacy
      .filter(
        (entry): entry is { name: string; model: GridFilterModel } =>
          typeof entry === "object" &&
          entry !== null &&
          typeof entry.name === "string",
      )
      .map((entry) => ({
        id: crypto.randomUUID(),
        name: entry.name,
        params: {},
        gridFilter: entry.model ?? null,
        createdAt: now,
        updatedAt: now,
      }))
  } catch {
    return []
  }
}

export function loadViews(userClientId: string): SavedView[] {
  try {
    const raw = localStorage.getItem(viewsStorageKey(userClientId))
    if (raw) {
      const stored = JSON.parse(raw) as StoredViews
      if (stored?.version === 1 && Array.isArray(stored.views)) {
        return stored.views
          .map(sanitizeView)
          .filter((view): view is SavedView => view !== null)
      }
      return []
    }
    const migrated = migrateLegacyFilters()
    if (migrated.length > 0) {
      saveViews(userClientId, migrated)
    }
    return migrated
  } catch {
    return []
  }
}

export function saveViews(userClientId: string, views: SavedView[]): void {
  try {
    const stored: StoredViews = { version: 1, views }
    localStorage.setItem(viewsStorageKey(userClientId), JSON.stringify(stored))
  } catch {
    // Quota or private-mode failure: the in-memory views still work for the
    // session; persistence silently degrades rather than crashing the page.
  }
}
